import { GetCurrentMember } from 'iam/SessionStore'
import { PromptDto } from 'jobs/prompts/dtos'
import once from 'lodash/once'
import { distinctUntilChanged } from 'rxjs/operators'
import { toStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { QuestionDTO } from '../dtos/questions'

import { timestampToISO } from 'utils/grpc-util'
import { match } from 'utils/pattern-match-util'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { Id } from 'types/definitions'
import { Model } from 'infra/model'

import {
  PromptQuestionInput as PromptQuestionInputProto,
  PromptDesignee as PromptDesigneeProto,
} from '@taxfyle/api-internal/internal/prompt_pb'
import { InputValue as InputValueProto } from '@taxfyle/api-internal/internal/question_input_value_pb'

import { computed, observable, makeObservable } from 'mobx'
import { PrimeMember } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import Job from 'jobs/Job'
import { PrimeJob } from 'jobs/JobStore'

/**
 * Represents the designee for a prompt.
 */
export enum PromptDesignee {
  Client,
  Provider,
  Unknown,
}

export class Prompt extends Model<PromptDto, Prompt> {
  id = Id.none

  @observable workspaceId = Id.none

  @observable title = ''
  @observable description = ''

  @observable createdBy = Member.none
  @observable answeredBy = Member.none
  @observable deletedBy = Member.none

  @observable designee: PromptDesignee = PromptDesignee.Unknown

  @observable dateCreated?: Date | null = DateZero()
  @observable dateAnswered?: Date | null = undefined
  @observable dateDeleted?: Date | null = undefined

  @observable job = Job.none

  // TODO: Refactor to use Question Models (which also need to be created).
  @observable question?: QuestionDTO = undefined

  @observable deleted = false

  /**
   * Stream for the prompt answer. Emits immediately on subscribe.
   */
  readonly answer$ = toStream<unknown>(() => this.question?.answer, true).pipe(
    distinctUntilChanged(),
    shareLatest()
  )

  constructor(
    private getCurrentMember: GetCurrentMember,
    private primeMember: PrimeMember,
    private primeJob: PrimeJob
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Default value to use for prompts.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * Whether the current user is allowed to answer the prompt.
   */
  @computed
  get canAnswer() {
    const currentMember = this.getCurrentMember(this.workspaceId)
    const hasAdminPermissionToAnswerPrompt =
      currentMember.hasPermission('ADMIN_JOB_UPDATE')
    const isJobClosed = this.job.status === 'CLOSED'
    return (
      this.isPendingResponse &&
      !this.isCreator &&
      !isJobClosed &&
      (this.isDesignee || hasAdminPermissionToAnswerPrompt)
    )
  }

  /**
   * Whether the current user is allowed to delete the prompt.
   */
  @computed
  get canDelete() {
    const currentMember = this.getCurrentMember(this.workspaceId)
    const hasAdminPermissionToDeletePrompt =
      currentMember.hasPermission('ADMIN_JOB_UPDATE')
    const isJobClosed = this.job.status === 'CLOSED'
    return (
      !this.dateDeleted &&
      !isJobClosed &&
      (!this.isDesignee || hasAdminPermissionToDeletePrompt)
    )
  }

  /**
   * Whether the prompt is pending a response.
   */
  @computed
  get isPendingResponse() {
    return !this.dateAnswered
  }

  /**
   * Whether the current user is the designee of the prompt.
   */
  @computed
  get isDesignee() {
    const designee = match(this.designee, {
      [PromptDesignee.Client]: () => 'CLIENT',
      [PromptDesignee.Provider]: () => 'PROVIDER',
      [PromptDesignee.Unknown]: () => 'UNKNOWN',
    })

    return this.job.currentUserCollaborator?.type === designee
  }

  /**
   * Whether the current user is the creator of the prompt.
   */
  @computed
  get isCreator() {
    const currentMember = this.getCurrentMember(this.workspaceId)
    return currentMember.user.publicId === this.createdBy.user.publicId
  }

  parse(attrs: Partial<PromptDto>): Partial<Prompt> {
    return {
      id: attrs.id,
      workspaceId: attrs.workspaceId,
      title: attrs.title,
      description: attrs.description,
      createdBy: this.primeMember({
        workspace_id: attrs.workspaceId,
        user_id: attrs.createdById,
        user_public_id: attrs.createdByPublicId,
      }),
      answeredBy: attrs.answeredById
        ? this.primeMember({
            workspace_id: attrs.workspaceId,
            user_id: attrs.answeredById,
            user_public_id: attrs.answeredByPublicId,
          })
        : Member.none,
      deletedBy: attrs.deletedById
        ? this.primeMember({
            workspace_id: attrs.workspaceId,
            user_id: attrs.deletedById,
            user_public_id: attrs.deletedByPublicId,
          })
        : Member.none,
      designee:
        attrs.designee !== undefined
          ? match(attrs.designee, {
              [PromptDesigneeProto.CLIENT]: () => PromptDesignee.Client,
              [PromptDesigneeProto.PROVIDER]: () => PromptDesignee.Provider,
              [PromptDesigneeProto.DESIGNEE_UNKNOWN]: () =>
                PromptDesignee.Unknown,
            })
          : undefined,
      dateCreated: maybeParseDate(timestampToISO(attrs.dateCreated)),
      dateAnswered: maybeParseDate(timestampToISO(attrs.dateAnswered)),
      dateDeleted: maybeParseDate(timestampToISO(attrs.dateDeleted)),
      job: this.primeJob({ id: attrs.jobId }),
      question: toQuestionDTO(
        attrs.id,
        attrs.title,
        attrs.description,
        attrs.question,
        attrs.answer
      ),
      deleted: attrs.deleted,
    }
  }
}

function toQuestionDTO(
  promptId?: string,
  title?: string,
  description?: string,
  question?: PromptQuestionInputProto.AsObject,
  answer?: InputValueProto.AsObject
): QuestionDTO | undefined {
  const baseProperties = {
    id: Id.assert(promptId),
    question: title,
    description,
  } as const

  if (question?.boolInput) {
    return {
      ...baseProperties,
      type: 'BOOL',
      answer: answer?.boolValue?.value,
    }
  }

  if (question?.documentsInput) {
    return {
      ...baseProperties,
      type: 'DOCUMENTS',
      answer: answer?.documentsValue?.selectedDocumentsList.map((item) => ({
        id: item.id,
        document_id: item.linkedDocumentId,
      })),
      options: question.documentsInput.optionsList,
    }
  }

  if (question?.textInput) {
    return {
      ...baseProperties,
      type: 'TEXT',
      answer: answer?.textValue?.value,
    }
  }

  return undefined
}

const lazyNone = once(
  () =>
    new Prompt(
      () => Member.none,
      () => Member.none,
      () => Job.none
    )
)
